import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ResponseTimerWidget } from '@/components/ResponseTimerWidget';
import { CalendarDays, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

const STAGE_COLORS = {
  new: '#3B82F6', contacted: '#8B5CF6', interested: '#F59E0B', test_drive: '#14B8A6',
  negotiation: '#EC4899', finance_pending: '#F97316', dp_pending: '#F59E0B', won: '#22C55E', lost: '#EF4444'
};
const STAGE_LABELS = {
  new: 'New Lead', contacted: 'Contacted', interested: 'Interested', test_drive: 'Test Drive',
  negotiation: 'Negotiation', finance_pending: 'Finance Approval', dp_pending: 'DP Pending', won: 'Won', lost: 'Lost'
};
const SOURCE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444'];

// Helpers for month date ranges — uses local dates (not UTC) to avoid timezone shift
const formatLocalDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getMonthRange = (offset = 0) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = offset === 0 ? now : new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return {
    from: formatLocalDate(first),
    to: formatLocalDate(last),
  };
};
const getMonthLabel = (offset) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleString('default', { month: 'short', year: 'numeric' });
};

function LeadsDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = React.useState(null);
  const [sourceData, setSourceData] = React.useState([]);
  const [repData, setRepData] = React.useState([]);
  const [recentLeads, setRecentLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Date filter state
  const currentMonth = getMonthRange(0);
  const [dateType, setDateType] = React.useState('created_date');
  const [dateFrom, setDateFrom] = React.useState(currentMonth.from);
  const [dateTo, setDateTo] = React.useState(currentMonth.to);
  const [activeTab, setActiveTab] = React.useState('current');

  React.useEffect(() => {
    fetchDashboardData();
  }, [dateFrom, dateTo, dateType]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (dateType) params.set('date_type', dateType);
      const qs = params.toString();

      const [analyticsRes, sourceRes, repRes, leadsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leads/analytics/dashboard?${qs}`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/leads/analytics/source-performance?${qs}`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/leads/analytics/rep-performance?${qs}`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/leads?limit=10`, getAuthHeader())
      ]);

      setAnalytics(analyticsRes.data);
      setSourceData(sourceRes.data);
      setRepData(repRes.data);
      setRecentLeads(leadsRes.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to fetch dashboard data');
    }
    setLoading(false);
  };

  const handleMonthTab = (tab) => {
    setActiveTab(tab);
    const offset = tab === 'last' ? -1 : tab === 'next' ? 1 : 0;
    const range = getMonthRange(offset);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  // Prepare funnel data
  const funnelData = analytics ? [
    { name: 'New', value: analytics.by_stage?.new || 0, fill: STAGE_COLORS.new },
    { name: 'Contacted', value: analytics.by_stage?.contacted || 0, fill: STAGE_COLORS.contacted },
    { name: 'Interested', value: analytics.by_stage?.interested || 0, fill: STAGE_COLORS.interested },
    { name: 'Test Drive', value: analytics.by_stage?.test_drive || 0, fill: STAGE_COLORS.test_drive },
    { name: 'Negotiation', value: analytics.by_stage?.negotiation || 0, fill: STAGE_COLORS.negotiation },
    { name: 'Won', value: analytics.by_stage?.won || 0, fill: STAGE_COLORS.won },
  ] : [];

  const sourcePieData = analytics?.by_source ?
    Object.entries(analytics.by_source).map(([name, value], idx) => ({
      name, value, fill: SOURCE_COLORS[idx % SOURCE_COLORS.length]
    })) : [];

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Dashboard">
      <div className="space-y-6">
        {/* Date Filter Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm" data-testid="lead-date-filter-bar">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Date Type Selector */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={dateType}
                onChange={e => setDateType(e.target.value)}
                className="px-3 py-1.5 border rounded-lg text-sm font-medium bg-gray-50 focus:ring-2 focus:ring-emerald-200"
                data-testid="date-type-select"
              >
                <option value="created_date">Created Date</option>
                <option value="follow_up_date">Follow Up Date</option>
              </select>
            </div>

            {/* Quick Month Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="month-tabs">
              <button
                onClick={() => handleMonthTab('last')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'last' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="month-tab-last"
              >
                <ChevronLeft className="w-3 h-3" /> {getMonthLabel(-1)}
              </button>
              <button
                onClick={() => handleMonthTab('current')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'current' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="month-tab-current"
              >
                {getMonthLabel(0)}
              </button>
              <button
                onClick={() => handleMonthTab('next')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'next' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="month-tab-next"
              >
                {getMonthLabel(1)} <ChevronRight className="w-3 h-3" />
              </button>
            </div>

            {/* Date Range Inputs */}
            <div className="flex items-center gap-2 ml-auto">
              <CalendarDays className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setActiveTab('custom'); }}
                className="px-2 py-1.5 border rounded-lg text-xs"
                data-testid="date-from-input"
              />
              <span className="text-xs text-gray-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setActiveTab('custom'); }}
                className="px-2 py-1.5 border rounded-lg text-xs"
                data-testid="date-to-input"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            {/* Top Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
                <p className="text-3xl font-bold">{analytics?.total || 0}</p>
                <p className="text-sm opacity-90">Total Leads</p>
              </div>
              <div className="rounded-2xl p-4 text-white shadow-[0_8px_28px_rgba(5,150,105,0.30)]" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}>
                <p className="text-3xl font-bold">{analytics?.this_month || 0}</p>
                <p className="text-sm opacity-90">This Month</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
                <p className="text-3xl font-bold">{analytics?.conversion_rate || 0}%</p>
                <p className="text-sm opacity-90">Conversion Rate</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg">
                <p className="text-3xl font-bold">{analytics?.by_priority?.hot || 0}</p>
                <p className="text-sm opacity-90">Hot Leads</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate('/leads/follow-up')}>
                <p className="text-3xl font-bold">{analytics?.follow_up_due_today || 0}</p>
                <p className="text-sm opacity-90">Follow-up Today</p>
              </div>
              <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-4 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate('/leads/follow-up')}>
                <p className="text-3xl font-bold">{analytics?.overdue_follow_ups || 0}</p>
                <p className="text-sm opacity-90">Overdue</p>
              </div>
            </div>

            {/* Stage Summary Cards */}
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {Object.entries(STAGE_LABELS).map(([stage, label]) => (
                <div
                  key={stage}
                  className="bg-white rounded-lg p-3 shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderLeftColor: STAGE_COLORS[stage] }}
                  onClick={() => navigate(`/leads/list?stage=${stage}`)}
                >
                  <p className="text-2xl font-bold" style={{ color: STAGE_COLORS[stage] }}>
                    {analytics?.by_stage?.[stage] || 0}
                  </p>
                  <p className="text-xs text-gray-600">{label}</p>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Lead Funnel */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 lg:col-span-2">
                <h3 className="font-bold text-gray-800 mb-4">Lead Funnel</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={funnelData} layout="vertical">
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={100} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {funnelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Lead Sources Pie */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-4">Lead Sources</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sourcePieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {sourcePieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Response Timer Widget */}
            <ResponseTimerWidget />

            {/* Bottom Row - Rep Performance & Recent Leads */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sales Rep Performance */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-800">Sales Rep Performance</h3>
                  <button onClick={() => navigate('/leads/reports/rep')} className="text-sm text-emerald-600 hover:underline">View Details →</button>
                </div>
                <div className="space-y-3">
                  {repData.slice(0, 5).map((rep, idx) => (
                    <div key={rep.rep} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{idx === 0 ? '1st' : idx === 1 ? '2nd' : idx === 2 ? '3rd' : `${idx + 1}th`}</span>
                        <div>
                          <p className="font-medium text-gray-800">{rep.rep}</p>
                          <p className="text-xs text-gray-500">{rep.assigned} leads assigned</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{rep.won} won</p>
                        <p className="text-xs text-gray-500">{rep.conversion_rate}% conv.</p>
                      </div>
                    </div>
                  ))}
                  {repData.length === 0 && <p className="text-center text-gray-500 py-4">No data available</p>}
                </div>
              </div>

              {/* Recent Leads */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-800">Recent Leads</h3>
                  <button onClick={() => navigate('/leads/list')} className="text-sm text-emerald-600 hover:underline">View All →</button>
                </div>
                <div className="space-y-2">
                  {recentLeads.slice(0, 6).map(lead => (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-emerald-50"
                      onClick={() => navigate(`/leads/edit/${lead.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          lead.priority === 'hot' ? 'bg-red-500' : lead.priority === 'warm' ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}>
                          {lead.first_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{lead.first_name} {lead.last_name || ''}</p>
                          <p className="text-xs text-gray-500">{lead.interested_model || 'No model specified'}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.stage === 'new' ? 'bg-blue-100 text-blue-700' :
                        lead.stage === 'won' ? 'bg-green-100 text-green-700' :
                        lead.stage === 'lost' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {STAGE_LABELS[lead.stage] || lead.stage}
                      </span>
                    </div>
                  ))}
                  {recentLeads.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-2">No leads yet</p>
                      <button onClick={() => navigate('/leads/add')} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">+ Add First Lead</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex justify-center gap-4 pt-4">
              <button onClick={() => navigate('/leads/add')} className="px-6 py-3 text-white rounded-xl font-medium shadow-[0_8px_24px_rgba(5,150,105,0.35)] hover:shadow-[0_12px_32px_rgba(5,150,105,0.50)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}>+ Add New Lead</button>
            </div>
          </>
        )}
      </div>
    </LeadsLayout>
  );
}

export default LeadsDashboard;
